import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Card,
    Table,
    Button,
    Modal,
    Form,
    Alert,
    Badge,
    Spinner,
    Row,
    Col
} from 'react-bootstrap';
import { Version, VersionStatus } from '../../Data/Versions';
import { versionService } from '../../../components/Version/services/versionService';
import { showSuccess, showError, showDeleteConfirm } from '../../../Utilities/sweetAlert';
import './VersionManagement.css';
import moment from 'moment';

const VersionManagement: React.FC = () => {
    const [versions, setVersions] = useState<Version[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newVersionString, setNewVersionString] = useState('');
    const [createLoading, setCreateLoading] = useState(false);

    const fetchVersions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await versionService.getAllVersions();
            // Backend returns { versions: [...] }
            // The response.data itself might be the response from Axios, so response.data.versions could be correct
            // But we need to match the updated service type definition.
            setVersions(response.data.versions || []);
        } catch (err) {
            console.error('Error fetching versions:', err);
            setError('Failed to load versions. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVersions();
    }, [fetchVersions]);

    const handleCreateVersion = async () => {
        const validation = versionService.validateVersionString(newVersionString);
        if (!validation.isValid) {
            showError('Invalid Version', validation.error || 'Invalid version format');
            return;
        }

        try {
            setCreateLoading(true);
            const response = await versionService.createVersion(newVersionString);
            // Backend returns serialized Version object directly
            const newVersion = response.data;
            setVersions(prev => [newVersion, ...prev]);
            setNewVersionString('');
            setShowCreateModal(false);
            showSuccess('Version Created', `Version "${newVersion.version}" created successfully.`);
        } catch (err: any) {
            console.error('Error creating version:', err);
            showError('Creation Failed', err.response?.data?.message || 'Failed to create version. It may already exist.');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleDeleteVersion = async (version: Version) => {
        // Client-side check for UX, though backend enforces it too
        if (version.status === VersionStatus.Unsupported) {
            showError('Cannot Delete', 'You cannot delete an unsupported version. Please mark it as supported first.');
            return;
        }

        const result = await showDeleteConfirm(
            'Delete Version?',
            `Are you sure you want to delete version "${version.version}"?`,
            'Yes, delete it',
            'Cancel'
        );

        if (result.isConfirmed) {
            try {
                await versionService.deleteVersion(version.id);
                setVersions(prev => prev.filter(v => v.id !== version.id));
                showSuccess('Version Deleted', `Version "${version.version}" has been deleted.`);
            } catch (err: any) {
                console.error('Error deleting version:', err);
                showError('Delete Failed', err.response?.data?.message || 'Failed to delete version.');
            }
        }
    };

    const handleToggleStatus = async (version: Version) => {
        const newStatus = version.status === VersionStatus.Supported ? VersionStatus.Unsupported : VersionStatus.Supported;
        try {
            const response = await versionService.updateVersionStatus(version.id, newStatus);
            // Backend returns serialized Version object directly
            const updatedVersion = response.data;

            setVersions(prev => prev.map(v => v.id === updatedVersion.id ? updatedVersion : v));
            showSuccess('Status Updated', `Version "${updatedVersion.version}" is now ${updatedVersion.status}.`);
        } catch (err: any) {
            console.error('Error updating status:', err);
            showError('Update Failed', err.response?.data?.message || 'Failed to update version status.');
        }
    };

    const supportedCount = (versions || []).filter(v => v.status === VersionStatus.Supported).length;
    const unsupportedCount = (versions || []).length - supportedCount;

    if (loading) {
        return (
            <Container fluid className="admin-page-container py-4">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" style={{ color: 'var(--primary-medium)' }}>
                        <span className="visually-hidden">Loading versions...</span>
                    </Spinner>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="admin-page-container py-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="admin-page-container py-4">
            <div className="version-management">
                <div className="page-header mb-4">
                    <Row>
                        <Col>
                            <h1>Version Management</h1>
                            <p className="text-muted">Manage application versions and forced updates.</p>
                        </Col>
                        <Col xs="auto">
                            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                                + Create Version
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Row className="mb-4">
                    <Col md={4}>
                        <Card className="stat-card">
                            <Card.Body className="text-center">
                                <h3 className="stat-number">{versions.length}</h3>
                                <p className="stat-label">Total Versions</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="stat-card">
                            <Card.Body className="text-center">
                                <h3 className="stat-number text-success">{supportedCount}</h3>
                                <p className="stat-label">Supported</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="stat-card">
                            <Card.Body className="text-center">
                                <h3 className="stat-number text-danger">{unsupportedCount}</h3>
                                <p className="stat-label">Unsupported (Forced Update)</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card>
                    <Card.Header>
                        <h5 className="mb-0">Versions List</h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <Table striped hover responsive>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Version</th>
                                    <th>Status</th>
                                    <th>Created At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {versions.map(version => (
                                    <tr key={version.id}>
                                        <td><Badge bg="secondary">#{version.id}</Badge></td>
                                        <td><strong>{version.version}</strong></td>
                                        <td>
                                            <Badge bg={version.status === VersionStatus.Supported ? 'success' : 'danger'}>
                                                {version.status.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>{moment(version.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                                        <td>
                                            <Button
                                                variant={version.status === VersionStatus.Supported ? "outline-warning" : "outline-success"}
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleToggleStatus(version)}
                                                title={version.status === VersionStatus.Supported ? "Mark as Unsupported" : "Mark as Supported"}
                                            >
                                                <i className={`fas ${version.status === VersionStatus.Supported ? 'fa-ban' : 'fa-check'}`}></i>
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteVersion(version)}
                                                title="Delete Version"
                                                disabled={version.status === VersionStatus.Unsupported}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* Create Version Modal */}
                <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Create New Version</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group>
                                <Form.Label>Version Number (X.Y.Z)</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. 1.0.0"
                                    value={newVersionString}
                                    onChange={(e) => setNewVersionString(e.target.value)}
                                    autoFocus
                                />
                                <Form.Text className="text-muted">
                                    Must be in semantic version format (e.g., 1.0.0). New versions are created as <strong>Unsupported</strong> by default.
                                </Form.Text>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={createLoading}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleCreateVersion} disabled={createLoading || !newVersionString}>
                            {createLoading ? <Spinner animation="border" size="sm" /> : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Container>
    );
};

export default VersionManagement;
